import { bound } from "@frui.ts/helpers/dist";
import { Modules } from "config";
import { action, computed } from "mobx";
import ChargesRepository from "repositories/chargesRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import EnumService from "services/enumService";
import ListViewModel from "viewModels/listViewModel";
import ChargePropertyChangeDisplayType from "entities/chargePropertyChangeDisplayType";
import type { interfaces } from "inversify";
import type ChargeResponse from "entities/chargeResponse";
import type { IResults } from "data/types";
import type ChargeChangeInitiator from "entities/chargeChangeInitiator";
import type ChargeChangeOrigin from "entities/chargeChangeOrigin";
import type FuelType from "entities/fuelType";
import ChargeChange from "entities/chargeChange";
import type VehicleRegisterVehicleFuelType from "entities/vehicleRegisterVehicleFuelType";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import PriceListService from "services/priceListService";

export default class ChargeHistoriesViewModel extends ListViewModel<ChargeChange> {
  readonly modelName = "chargeHistory";

  constructor(
    public charge: ChargeResponse,
    public localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: ChargesRepository,
    private enumService: EnumService,
    private priceListService: PriceListService
  ) {
    super();
    this.navigationName = "";
  }

  protected async fetchData(): Promise<IResults<ChargeChange>> {
    return await this.repository.getAllChanges(this.charge);
  }

  @computed
  get transformedItems(): ChargeChange[] {
    return this.items.flatMap(item => {
      return item;
    });
  }

  @bound
  canShowDetail(charge: ChargeChange): boolean {
    return (
      charge.changeType === ChargePropertyChangeDisplayType.VehicleRegisterVehicleIdChange ||
      charge.changeType === ChargePropertyChangeDisplayType.VehicleRegisterVehiclePropertiesChange
    );
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @action.bound
  handleShowDetail(change: ChargeChange): void {
    change._showDetail = !change._showDetail;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `chargeHistories.${code}`, size);
  }

  translateChangeAttribute(attribute: keyof ChargeChange) {
    return this.localizationService.translateAttribute("chargeChange", attribute);
  }

  translateFuelType(priceListId: string): string {
    const fuelTypeId = this.priceListService.priceListItemById(priceListId)?.fuelTypeId as FuelType;

    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === fuelTypeId);
    if (!fuelType) {
      return "";
    }

    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.name;
  }

  translateBoolean(boolean: string) {
    return this.localizationService.translateGeneral(`boolean.${boolean.toLowerCase()}`);
  }

  translateChangedChargeProperty(change: ChargeChange, chargePropertyChangeDisplayType: ChargePropertyChangeDisplayType) {
    if (
      change.wasMadeByCustomerDuringValidity &&
      chargePropertyChangeDisplayType === ChargePropertyChangeDisplayType.LicensePlateChangeDuringValidity
    ) {
      return this.translate("licensePlateChangeDuringValidity");
    } else if (
      change.wasMadeByCustomerDuringValidity &&
      chargePropertyChangeDisplayType === ChargePropertyChangeDisplayType.CountryChangeDuringValidity
    ) {
      return this.translate("countryIdChangeDuringValidity");
    } else {
      return this.localizationService.translateEnum("chargePropertyChangeDisplayType", chargePropertyChangeDisplayType);
    }
  }

  translateChargeChangeInitiator(chargeChangeInitiator: ChargeChangeInitiator) {
    return this.localizationService.translateEnum("chargeChangeInitiator", chargeChangeInitiator);
  }

  translateChargeChangeOrigin(chargeChangeOrigin: ChargeChangeOrigin) {
    return this.localizationService.translateEnum("chargeChangeOrigin", chargeChangeOrigin);
  }

  translateVehicleRegisterVehicleFuelType(fuelType: VehicleRegisterVehicleFuelType[]) {
    const translation = fuelType.map(f => this.localizationService.translateEnum("vehicleRegisterVehicleFuelType", f));
    return translation.length > 1 ? translation.join(", ") : translation[0];
  }

  translateVehicleRegisterVehicleClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  translateVehicleStatus(status: VehicleRegisterVehicleStatus) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleStatus", status);
  }

  static Factory({ container }: interfaces.Context) {
    return (charge: ChargeResponse) =>
      new ChargeHistoriesViewModel(
        charge,
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(ChargesRepository),
        container.get(EnumService),
        container.get(PriceListService)
      );
  }
}
